import { useState } from "react";
import { login } from "../api/auth";
import type { Session } from "../api/auth";

/**
 * Sign-in gate.
 *
 * `session` is passed in so the screen can tell the reader *why* a sign-in is
 * impossible when it is — an unconfigured server is not a wrong password, and
 * showing "incorrect username" for it would send someone hunting for a
 * credential that does not exist.
 */
export function Login({ session, onSignedIn }: { session: Session; onSignedIn: () => void }) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await login(username, password);
            onSignedIn();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const blocked = !session.login_available;

    return (
        <div className="login-shell">
            <div className="login-card">
                <div className="login-mark" aria-hidden="true">PPD</div>
                <div className="login-brand">PPD Agent Platform</div>
                <h1 className="login-title">Sign in</h1>
                <p className="login-sub">
                    This console shows extracted KYC data and the original identity
                    documents. Access is recorded against your account.
                </p>

                {blocked ? (
                    <div className="notice notice-warn" style={{ marginBottom: 0 }}>
                        <strong>Sign-in is not available on this server.</strong>
                        <div style={{ marginTop: 8 }}>
                            {!session.configured
                                ? "No credentials are configured, so the API is refusing every request."
                                : !session.session_secret_set
                                  ? "SESSION_SECRET is unset or shorter than 32 characters, so sessions cannot be signed."
                                  : "No interactive users are configured; only service tokens are accepted."}
                            <div className="mono" style={{ marginTop: 10 }}>
                                docker exec -it ppd_preprocessor python -m src.scripts.hash_password
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="login-form">
                        {/* The error sits above the button, not below it: after a
                            failed attempt the button is where the eye already is. */}
                        {error ? (
                            <div className="login-error" role="alert">{error}</div>
                        ) : null}

                        {/* Either identifier works -- the server matches both in
                            one query -- so the label says so rather than making
                            someone guess which one their account uses. Still
                            type="text": type="email" would have the browser
                            refuse a bare username before it is ever sent. */}
                        <label htmlFor="u">Username or email</label>
                        <input
                            id="u"
                            name="username"
                            type="text"
                            autoComplete="username"
                            spellCheck={false}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            autoFocus
                        />

                        <label htmlFor="p">Password</label>
                        <input
                            id="p"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />

                        <button type="submit" className="btn-primary" disabled={busy}>
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                )}

                {session.mode === "disabled" ? (
                    <div className="notice notice-warn" style={{ marginTop: 16, marginBottom: 0 }}>
                        <strong>Authentication is disabled on this server.</strong> Every
                        /api/orchestrator route is public, including the original document scans.
                    </div>
                ) : null}

                {!blocked ? (
                    <div className="login-foot">
                        Accounts are configured in <span className="mono">ORCHESTRATOR_UI_USERS</span>{" "}
                        in the platform <span className="mono">.env</span>.
                    </div>
                ) : null}
            </div>
        </div>
    );
}
